import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { LoadingId, Timesheet } from '../../../types';
import { styled, useTheme } from '@mui/material';
import { Typography } from '../../../components/Typography/Typography';
import { useLoadingBar } from '../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../utils/errorNotifier';
import { formatTSWeekFormat } from '../../../utils/formatters';
import { useMemo } from 'react';
import { useWithdrawTimesheet } from '../../../queries/hooks/useWithdrawTimesheet';

type Props = {
  timesheet?: Timesheet;
  toggleModal: () => void;
  isOpen: boolean;
};

export function ConfirmWithdrawModal({ timesheet, isOpen, toggleModal }: Props) {
  const { colors } = useTheme();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();

  const weekName = useMemo(() => {
    if (timesheet?.weekDates?.length) {
      return formatTSWeekFormat(
        timesheet?.weekDates[0],
        timesheet?.weekDates[timesheet?.weekDates.length - 1]
      );
    }
    if (timesheet?.holidays?.length) {
      return formatTSWeekFormat(
        `${timesheet?.holidays[0].year}-${timesheet?.holidays[0].month}-${timesheet?.holidays[0].day}`,
        `${timesheet?.holidays[timesheet?.holidays.length - 1].year}-${
          timesheet?.holidays[timesheet?.holidays.length - 1].month
        }-${timesheet?.holidays[timesheet?.holidays.length - 1].day}`
      );
    }
    return '';
  }, [timesheet?.holidays, timesheet?.weekDates]);

  const { mutate: onWithdrawTimesheet, isLoading: saveInProgress } = useWithdrawTimesheet();

  const handleSubmitTimesheet = async () => {
    startLoading(LoadingId.withdrawTimesheet);
    onWithdrawTimesheet(
      {
        id: timesheet?.id,
      },
      {
        onSuccess: () => {
          pushSuccessToast({
            title: 'Timesheet Withdrawn',
            message: `${weekName} has been successfully withdrawn`,
          });
          toggleModal();
        },
        onError: (error: unknown) => {
          pushErrorToast({ message: extractErrorMessage(error) });
        },
        onSettled: () => {
          stopLoading(LoadingId.withdrawTimesheet);
        },
      }
    );
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={toggleModal}
      actionButtonsProps={{
        isSubmitDisabled: saveInProgress,
        onSubmit: handleSubmitTimesheet,
        onCancel: toggleModal,
        submitText: 'Withdraw Timesheet',
      }}
      title={`Withdraw Timesheet`}
      top='80px'
    >
      <InfoWrapper>
        <Typography variant='body' color={colors.text.main}>
          {`Are you sure you want to withdraw ${weekName} timesheet?
The Timesheet will be sent back for review.`}
        </Typography>
      </InfoWrapper>
    </ModalComponent>
  );
}

const InfoWrapper = styled('div')`
  border-radius: 2px;
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;
